import { h, render, Component } from 'preact';
import { observer } from 'mobx-preact';
import { IntlProvider, Text } from 'preact-i18n';
import util from 'preact-util';

import { printViaIframe } from '../../lib/printHelper';
import DentalChart, { isCatSpecies, isDogSpecies, isHorseSpecies } from './dentalChart';
import DentalChartPrint from './dentalChartPrint';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

@observer
class DentalChartSection extends Component {
    constructor(props) {
        super(props);
        this.state = {
            expanded: false,
            saving: false,
        };
        this.saveTimer = null;
    }

    componentWillUnmount() {
        this._unmounted = true;
        if (this.saveTimer) {
            clearTimeout(this.saveTimer);
        }
    }

    safeSetState = (next) => {
        if (this._unmounted) return;
        this.setState(next);
    };

    handleChange = (newFindings) => {
        const { stores, objectName = 'journal' } = this.props;
        const { journalStore } = stores;
        journalStore.updateObjectKeyValue(objectName, 'dentalFindings', newFindings);
        // Defer to a microtask so we don't trigger a setState while Preact
        // is still finishing the current render cycle started by the click.
        Promise.resolve().then(() => this.scheduleSave(newFindings));
    };

    scheduleSave = (newFindings) => {
        const { journal } = this.props;
        if (!journal || !journal.id) return;
        if (this._unmounted) return;
        if (this.saveTimer) {
            clearTimeout(this.saveTimer);
        }
        this.safeSetState({ saving: true });
        this.saveTimer = setTimeout(async () => {
            try {
                await util.fetchApi(`/api/journals/${journal.id}`, { method: 'PATCH' }, {
                    'dentalFindings-overwrite': newFindings,
                });
            } finally {
                this.safeSetState({ saving: false });
            }
        }, 750);
    };

    toggleExpanded = () => {
        this.setState(s => ({ expanded: !s.expanded }));
    };

    // Standalone tannkart printout (TICKET-01561). Mirrors onClickPrintJournal in
    // viewJournal.js/newJournal.js: render off-screen, hand the HTML to the
    // iframe print helper. Works with zero findings so a blank chart can be
    // printed and filled in by hand.
    onClickPrint = (e) => {
        e.stopPropagation();
        const { journal = {}, animal = {} } = this.props;
        const { userStore = {}, journalStore = {} } = this.props.stores || {};
        const { user = {}, customer = {} } = userStore;
        const { owners = [] } = journalStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language] || countryMap.default;

        const owner = owners.find(o => o.id === animal.owner) || owners[0];
        const logo = customer.images && customer.images.length > 0 ? customer.images[0] : null;
        const logoSrc = logo && typeof logo === 'object' ? logo.s3SmallLink : undefined;

        const page = <IntlProvider definition={languageDef}>
            <DentalChartPrint
                customer={customer}
                owner={owner}
                animal={animal}
                findings={journal.dentalFindings || []}
                logoSrc={logoSrc}
            />
        </IntlProvider>;

        const tempDiv = document.createElement('div');
        tempDiv.className = 'receipt';
        document.body.appendChild(tempDiv);
        render(page, tempDiv);
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printViaIframe(htmlContent, { waitForImages: !!logoSrc });
    };

    render() {
        const { journal = {}, animal = {}, readOnly = false } = this.props;
        const { expanded, saving } = this.state;

        const cat = isCatSpecies(animal.species, animal.speciesId);
        const dog = isDogSpecies(animal.species, animal.speciesId);
        const horse = isHorseSpecies(animal.species, animal.speciesId);
        if (!cat && !dog && !horse) {
            return null;
        }

        const findings = journal.dentalFindings || [];
        const count = findings.length;

        return (
            <div class='mx-2 mt-3 mb-4 p-3 rounded border-start border-primary bg-body-tertiary' style='border-left-width: 4px !important; background-color: rgba(0,0,0,0.06) !important;'>
                <div
                    class='fw-semibold mb-2 d-flex justify-content-between align-items-center'
                    style='cursor: pointer;'
                    onClick={this.toggleExpanded}
                >
                    <div>
                        <i class='fa-duotone fa-tooth me-2 text-primary' />
                        <Text id='dental.sectionTitle'>Dental Chart</Text>
                        {count > 0 && <span class='badge bg-secondary ms-2'>{count}</span>}
                        {saving && (
                            <span class='ms-2 small text-body-secondary'>
                                <i class='fa-duotone fa-spinner fa-spin me-1' />
                                <Text id='input.saving'>Saving...</Text>
                            </span>
                        )}
                    </div>
                    <div class='d-flex align-items-center'>
                        <button
                            type='button'
                            class='btn btn-sm btn-outline-secondary me-2'
                            onClick={this.onClickPrint}
                        >
                            <i class='fa-solid fa-print' />
                            <span class='d-none d-sm-inline ms-1'><Text id='dental.printChart'>Print dental chart</Text></span>
                        </button>
                        <i class={`fa-solid fa-chevron-${expanded ? 'up' : 'down'}`} />
                    </div>
                </div>
                {expanded && (
                    <div>
                        <DentalChart
                            species={animal.species}
                            speciesId={animal.speciesId}
                            findings={findings}
                            onChange={readOnly ? undefined : this.handleChange}
                            readOnly={readOnly}
                        />
                        {!readOnly && (
                            <div class='small text-body-secondary mt-2'>
                                <Text id='dental.autosave'>Changes are saved automatically</Text>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export default DentalChartSection;
